import React, { createContext, useReducer, useCallback, useContext, useEffect, Reducer, ReactNode } from 'react';
import Auth from '@aws-amplify/auth';
import { User } from '../../types/users';

const AuthenticationContext = createContext<{
  loading: boolean;
  currentUser: null | User;
  setLoggedInUser: (user: null | User) => void;
  logOut: () => Promise<void>;
}>({
  loading: false,
  currentUser: null,
  setLoggedInUser: () => {},
  logOut: async () => {},
});

type State = { loading: boolean; currentUser: null | User };

const initialState = {
  loading: true,
  currentUser: null,
};

type Actions = { type: 'LOGGED_IN'; payload: any } | { type: 'LOGGED_OUT' };

const reducer: Reducer<State, Actions> = (state = initialState, action) => {
  switch (action.type) {
    case 'LOGGED_IN':
      console.log('logged in as', JSON.stringify(action.payload.attributes));
      return {
        loading: false,
        currentUser: { username: action.payload.username, role: action.payload.attributes['custom:role'] },
      };
    case 'LOGGED_OUT':
      console.log('logged out');
      return { loading: false, currentUser: null };
    default:
      return state;
  }
};

export const AuthenticationProvider = (props: { children: ReactNode }) => {
  const [{ loading, currentUser }, dispatch] = useReducer(reducer, initialState);

  const setLoggedInUser = useCallback(
    user => {
      dispatch({ type: 'LOGGED_IN', payload: user });
    },
    [dispatch]
  );

  const logOut = useCallback(async () => {
    await Auth.signOut();
    dispatch({ type: 'LOGGED_OUT' });
  }, [dispatch]);

  const initializeAuth = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      return setLoggedInUser(user);
    } catch (e) {
      dispatch({ type: 'LOGGED_OUT' });
    }
  }, [setLoggedInUser, dispatch]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <AuthenticationContext.Provider value={{ loading, currentUser, setLoggedInUser, logOut }}>
      {props.children}
    </AuthenticationContext.Provider>
  );
};

export const useAuthenticationContext = () => useContext(AuthenticationContext);
export const useCurrentUser = () => useAuthenticationContext().currentUser;
export const useAuthenticationLoading = () => useAuthenticationContext().loading;
